import { Box, Typography, useTheme, useMediaQuery } from '@mui/material'
import { PieChart, Pie, Sector, ResponsiveContainer, Cell } from 'recharts'
import { useState, useEffect } from 'react'

interface PieNode {
  name: string
  value: number
  color: string
  originalValue: number
  parentLabel?: string
}

interface BudgetItem {
  id: number
  label: string
  amount: string
  parent_id?: number
  parentId?: number
}

interface BudgetPieChartProps {
  data: BudgetItem[]
}

const getColorPalette = (theme: any) => [
  theme.palette.primary.main,
  theme.palette.secondary.main,
  theme.palette.warning.main,
]

const renderActiveShape = (props: any) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props

  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
    </g>
  )
}

const renderCenterLabel = (activeItem: PieNode | null, isMobile: boolean) => {
  if (!activeItem) return null

  return (
    <text
      x="50%"
      y="46%"
      textAnchor="middle"
      dominantBaseline="middle"
      fill="#333"
      fontSize={isMobile ? 14 : 16}
      fontWeight="bold"
    >
      {activeItem.name}
      <tspan x="50%" dy={isMobile ? 18 : 24} fontSize={isMobile ? 12 : 14} fill="#666">
        ${activeItem.originalValue.toFixed(2)}
      </tspan>
      <tspan x="50%" dy={isMobile ? 16 : 20} fontSize={isMobile ? 10 : 12} fill="#999">
        ({(activeItem.value / activeItem.value * 100).toFixed(1)}%)
      </tspan>
    </text>
  )
}

export default function BudgetPieChart({ data }: BudgetPieChartProps) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [activeLevel, setActiveLevel] = useState<number>(0)
  const [activeIndex, setActiveIndex] = useState<number>(-1)
  const [chartData, setChartData] = useState<{ level: PieNode[], label: string }[]>([])

  const COLOR_PALETTE = getColorPalette(theme)

  useEffect(() => {
    const buildHierarchy = (): { level: PieNode[], label: string }[] => {
      const levels: { level: PieNode[], label: string }[] = []
      const itemMap = new Map<number, BudgetItem>()
      data.forEach(item => itemMap.set(item.id, item))

      const rootItems = data.filter(item => {
        const parentId = item.parent_id ?? item.parentId
        return !parentId || !itemMap.has(parentId)
      })

      levels.push({
        level: rootItems.map((item, idx) => ({
          name: item.label,
          value: Math.abs(parseFloat(item.amount)),
          color: COLOR_PALETTE[idx % COLOR_PALETTE.length],
          originalValue: parseFloat(item.amount),
          parentLabel: "Overview"
        })),
        label: "Overview"
      })

      const parentItems = data.filter(item => {
        const hasChildren = data.some(child => {
          const childParentId = child.parent_id ?? child.parentId
          return childParentId === item.id
        })
        return hasChildren
      })

      parentItems.forEach(parent => {
        const children = data.filter(item => {
          const itemParentId = item.parent_id ?? item.parentId
          return itemParentId === parent.id
        })

        if (children.length > 0) {
          levels.push({
            level: children.map((item, idx) => ({
              name: item.label,
              value: Math.abs(parseFloat(item.amount)),
              color: COLOR_PALETTE[(idx + levels[0].level.length) % COLOR_PALETTE.length],
              originalValue: parseFloat(item.amount),
              parentLabel: parent.label
            })),
            label: parent.label
          })
        }
      })

      return levels
    }

    if (data?.length > 0) {
      try {
        const hierarchy = buildHierarchy()
        setChartData(hierarchy)
        setActiveLevel(0)
      } catch (error) {
        console.error('Error building hierarchy:', error)
        setChartData([])
      }
    } else {
      setChartData([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const handleLevelChange = (level: number) => {
    if (level >= 0 && level < chartData.length) {
      setActiveLevel(level)
      setActiveIndex(-1)
    }
  }

  const onPieClick = (_: any, index: number) => {
    setActiveIndex(index === activeIndex ? -1 : index)
  }

  if (!chartData || chartData.length === 0) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '300px'
      }}>
        <Typography>
          {data?.length > 0 ? 'Processing data...' : 'No data available'}
        </Typography>
      </Box>
    )
  }

  const activeItem = activeIndex >= 0 ? chartData[activeLevel].level[activeIndex] : null

  return (
    <Box sx={{
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      p: 2
    }}>
      <Typography variant="h6" gutterBottom sx={{ textAlign: 'center' }}>
        Budget Summary
      </Typography>

      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        mb: 2,
        flexWrap: 'wrap',
        gap: 1
      }}>
        {chartData.map((levelData, index) => (
          <Box
            key={index}
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              bgcolor: activeLevel === index ? theme.palette.primary.main : 'action.hover',
              color: activeLevel === index ? 'common.white' : 'text.primary',
              cursor: 'pointer',
              fontSize: isMobile ? '0.75rem' : '0.875rem',
              '&:hover': {
                bgcolor: activeLevel === index ? theme.palette.primary.dark : 'action.selected'
              }
            }}
            onClick={() => handleLevelChange(index)}
          >
            {levelData.label}
          </Box>
        ))}
      </Box>

      <Box sx={{
        width: '100%',
        height: isMobile ? '350px' : '450px',
        minHeight: isMobile ? '350px' : '450px',
        position: 'relative'
      }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              activeIndex={activeIndex}
              activeShape={renderActiveShape}
              data={chartData[activeLevel].level}
              cx="50%"
              cy="50%"
              innerRadius={isMobile ? '65%' : '45%'}
              outerRadius={isMobile ? '100%' : '80%'}
              dataKey="value"
              onClick={onPieClick}
              animationDuration={300}
            >
              {chartData[activeLevel].level.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.color}
                  stroke={theme.palette.background.paper}
                  strokeWidth={1}
                />
              ))}
            </Pie>
            {activeItem && renderCenterLabel(activeItem, isMobile)}
          </PieChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  )
}
